import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Paper, Typography, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Tooltip, Autocomplete, Chip, InputBase, Select, MenuItem } from "@mui/material"
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUserModels } from '../context/UserModelsContext';
import { useRole, useUser } from '../context/UserContext';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { BACKEND_URL, APP_TOP_BAR_HEIGHT } from '../utils/constants';
import OnboardingStepOne from '../components/OnboardingStepOne';
import OnboardingStepTwo from '../components/OnboardingStepTwo';
import OnboardingStepThree from '../components/OnboardingStepThree';
import { useTheme } from '@mui/material/styles';
import { colors, topBarSx } from '../theme';
import { COLOR_PALETTE, getTagColor } from '../utils/tagColors';

import { ConstructionLoader } from '../components/ConstructionLoader';
interface TagDefinition {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_pipeline_stage: boolean;
  status: string;
}

// Option shown in the tag dialog / filter dropdown, derived from a definition.
interface TagOption {
  name: string;
  color: string | null;
  isStage: boolean;
  stageNumber: number | null; // 1-based pipeline position for stages
}

const Home = () => {
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();
  // Enterprise batch 8: viewers see every firm deal but get no create, edit,
  // delete or tag controls (the server refuses those writes regardless), and
  // a firm of more than one person shows who created each deal.
  const { canEdit, inFirm } = useRole();
  const { userModels, setUserModels } = useUserModels();
  const [view, setView] = useState('cards');
  const [orderBy, setOrderBy] = useState<string>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteName, setPendingDeleteName] = useState<string | null>(null);

  const [subLoading, setSubLoading] = useState(true);
  const [isSubActive, setIsSubActive] = useState<boolean>(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [eligibleForTrial, setEligibleForTrial] = useState<boolean>(true);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean>(false);
  const [onboardingData, setOnboardingData] = useState<any>(null);
  const [onboardingStep, setOnboardingStep] = useState<number>(1);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagTargetModelId, setTagTargetModelId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState<string>("");
  const [tagColor, setTagColor] = useState<string>(COLOR_PALETTE[0]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [tagDefinitions, setTagDefinitions] = useState<TagDefinition[]>([]);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user) return;
      try {
        const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
        const r = await fetch(`${BACKEND_URL}/api/user_info`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        });
        if (!r.ok) {
          setNeedsOnboarding(false);
          return;
        }
        const data = await r.json();
        const missingUserInfo = Boolean(data && data.exists === false);
        const termsNotAccepted = Boolean(data && data.exists !== false && data.accepted_terms_and_conditions !== true);
        setNeedsOnboarding(missingUserInfo || termsNotAccepted);
      } catch {
        setNeedsOnboarding(false);
      }
    };
    checkOnboarding();
  }, [user, getAccessTokenSilently]);

  const handleOnboardingComplete = async (data: any) => {
    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
      await fetch(`${BACKEND_URL}/api/user_info`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_role: data.jobRole || '',
          company: data.company || '',
          experience_level: data.experienceLevel || '',
          asset_type_focus: data.assetTypeFocus || [],
          typical_deal_size: data.typicalDealSize || '',
          geographic_focus: data.geographicFocus || '',
          hear_about_us: data.referralSource || '',
          keep_updated: Boolean(data.emailUpdates)
        })
      });
      // Profile is saved; plan choice is the last step. Onboarding stays open
      // so step 3 can show, and is dismissed by choosing a plan or skipping.
      // Someone who joined a firm works on the firm's plan (batch 7), so the
      // plan step is skipped for them.
      if (inFirm) setNeedsOnboarding(false);
      else setOnboardingStep(3);
    } catch (e) {
      console.error('Failed to save onboarding info', e);
      // Don't strand the user on a failed profile save — let them pick a plan.
      if (inFirm) setNeedsOnboarding(false);
      else setOnboardingStep(3);
    }
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) return;
      try {
        setSubLoading(true);
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
        });
        const r = await fetch(`${BACKEND_URL}/api/billing/subscription`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        });
        let data: any = null;
        try {
          data = await r.json();
        } catch {
          data = null;
        }
        if (r.ok && data) {
          const status = data.status as string | undefined;
          setSubscriptionStatus(status || null);
          setIsSubActive(status === 'active' || status === 'trialing');
          setEligibleForTrial(Boolean(data.eligible_for_trial));
        } else {
          setIsSubActive(false);
          if (data && typeof data.eligible_for_trial === 'boolean') {
            setEligibleForTrial(data.eligible_for_trial);
          } else {
            setEligibleForTrial(true);
          }
        }
      } catch {
        setIsSubActive(false);
        setSubscriptionStatus(null);
        setEligibleForTrial(true);
      } finally {
        setSubLoading(false);
      }
    };
    fetchSubscription();
  }, [user, getAccessTokenSilently]);

  useEffect(() => {
    const fetchUserModels = async () => {
      if (!user) return;
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(BACKEND_URL + `/api/user_models?user_id=${user?.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });
        const data = await response.json();

        if (JSON.stringify(data) !== JSON.stringify(userModels)) {
          setUserModels(data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user models:', err);
        setLoading(false);
      }
    };

    fetchUserModels();
  }, [user, userModels, setUserModels, getAccessTokenSilently]);

  // Canonical, user-defined pipeline tags (DPL-001). Drives the tag dialog
  // options, filter ordering, colors, and the deal "current stage" display.
  const fetchTagDefinitions = React.useCallback(async () => {
    if (!user) return;
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${BACKEND_URL}/api/tag_definitions`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!r.ok) return;
      const data = await r.json();
      if (Array.isArray(data)) setTagDefinitions(data);
    } catch {
      // Non-fatal: fall back to legacy getTagColor()/free-text behavior.
    }
  }, [user, getAccessTokenSilently]);

  useEffect(() => { fetchTagDefinitions(); }, [fetchTagDefinitions]);

  // Active definitions split into ordered pipeline stages and labels.
  const orderedDefinitions = React.useMemo(() => {
    const active = (tagDefinitions || []).filter((d) => d.status === 'active');
    const stages = active.filter((d) => d.is_pipeline_stage).sort((a, b) => a.sort_order - b.sort_order);
    const labels = active.filter((d) => !d.is_pipeline_stage).sort((a, b) => a.sort_order - b.sort_order);
    return { stages, labels, all: [...stages, ...labels] };
  }, [tagDefinitions]);

  // name (lowercased) → definition
  const tagDefByName = React.useMemo(() => {
    const map = new Map<string, TagDefinition>();
    orderedDefinitions.all.forEach((d) => { map.set(d.name.toLowerCase().trim(), d); });
    return map;
  }, [orderedDefinitions]);

  // name (lowercased) → 1-based pipeline stage number
  const stageNumberByName = React.useMemo(() => {
    const map = new Map<string, number>();
    orderedDefinitions.stages.forEach((d, i) => map.set(d.name.toLowerCase().trim(), i + 1));
    return map;
  }, [orderedDefinitions]);

  // Color resolution: definition color wins; fall back to legacy stored color /
  // hash for tags that have no definition yet.
  const colorForTag = React.useCallback((name: string, storedColor?: string | null): string => {
    const def = tagDefByName.get(String(name).toLowerCase().trim());
    if (def?.color) return def.color;
    return getTagColor(name, storedColor);
  }, [tagDefByName]);

  const models = userModels
    .filter((m: any) => m.active !== false)
    .map((model: any) => ({
    id: model.id,
    name: model.name,
    location: `${model.city}, ${model.state}`,
    created_at: model.created_at ? new Date(model.created_at).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) : 'N/A',
    created_at_raw: (() => {
      if (!model.created_at) return 0;
      const t = new Date(model.created_at).getTime();
      return Number.isFinite(t) ? t : 0;
    })(),
    created_date: model.created_at ? new Date(model.created_at).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }) : 'N/A',
    updated_at: model.updated_at ? new Date(model.updated_at).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }) : 'N/A',
    updated_at_raw: (() => {
      if (!model.updated_at) return 0;
      const t = new Date(model.updated_at).getTime();
      return Number.isFinite(t) ? t : 0;
    })(),
    irr: model.levered_irr && !String(model.levered_irr).startsWith('#') ? `${model.levered_irr}` : 'N/A',
    moic: model.levered_moic && !String(model.levered_moic).startsWith('#') ? `${model.levered_moic}` : 'N/A',
    version_count: model.version_count,
    model_type: model.model_type,
    created_by: model.created_by_me ? 'You' : (model.owner_email || 'Unknown'),
    ...(() => {
      const tags = Array.isArray(model.model_tags)
        ? model.model_tags
            .filter((t: any) => t && t.tag_name && t.status !== 'removed')
            .map((t: any) => ({ id: t.id, name: t.tag_name, color: t.tag_color || null }))
        : [];
      // Current stage = the deal's active tag that maps to a pipeline-stage
      // definition with the highest sort_order. Other tags (labels, lower
      // stages, legacy tags without a definition) are secondary.
      let currentStage: any = null;
      let bestOrder = -Infinity;
      tags.forEach((t: any) => {
        const def = tagDefByName.get(String(t.name).toLowerCase().trim());
        if (def && def.is_pipeline_stage && def.sort_order > bestOrder) {
          bestOrder = def.sort_order;
          currentStage = {
            ...t,
            sortOrder: def.sort_order,
            stageNumber: stageNumberByName.get(String(t.name).toLowerCase().trim()) ?? null,
          };
        }
      });
      const otherTags = currentStage ? tags.filter((t: any) => t.id !== currentStage.id) : tags;
      return {
        tags,
        current_stage: currentStage,
        current_stage_order: currentStage ? currentStage.sortOrder : null,
        other_tags: otherTags,
      };
    })(),
  }));

  // Distinct legacy tag names seen on models (alphabetical) — kept so tags that
  // predate the definitions table remain filterable.
  const allTags = React.useMemo(() => {
    const set = new Set<string>();
    (userModels || []).forEach((m: any) => {
      (m?.model_tags || [])
        .filter((t: any) => t && t.status !== 'removed')
        .forEach((t: any) => {
          if (t?.tag_name) set.add(String(t.tag_name));
        });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [userModels]);

  // Filter dropdown options in pipeline order: definitions first (stages, then
  // labels), then any legacy tag names without a definition.
  const orderedFilterOptions = React.useMemo(() => {
    const result: string[] = [];
    const seen = new Set<string>();
    orderedDefinitions.all.forEach((d) => {
      const k = d.name.toLowerCase().trim();
      if (!seen.has(k)) { seen.add(k); result.push(d.name); }
    });
    allTags.forEach((name) => {
      const k = name.toLowerCase().trim();
      if (!seen.has(k)) { seen.add(k); result.push(name); }
    });
    return result;
  }, [orderedDefinitions, allTags]);

  // Lookup: tag name → stored color (from any model's tags)
  const tagNameColorMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    (userModels || []).forEach((m: any) => {
      (m?.model_tags || [])
        .filter((t: any) => t && t.status !== 'removed' && t.tag_color)
        .forEach((t: any) => {
          if (t.tag_name && !map[t.tag_name]) map[t.tag_name] = t.tag_color;
        });
    });
    return map;
  }, [userModels]);

  // Auto-populate color picker when the typed name matches a definition (its
  // color wins) or, for legacy tags, a previously stored color.
  React.useEffect(() => {
    const name = tagInput.trim();
    if (!name) return;
    const def = tagDefByName.get(name.toLowerCase().trim());
    if (def?.color) {
      setTagColor(def.color);
    } else if (tagNameColorMap[name]) {
      setTagColor(tagNameColorMap[name]);
    }
  }, [tagInput, tagDefByName, tagNameColorMap]);

  const filteredModels = React.useMemo(() => {
    let result = models;
    if (tagFilter && tagFilter.length > 0) {
      const selected = tagFilter.map(t => String(t).toLowerCase().trim());
      result = result.filter((m: any) => {
        const tagSet = new Set((m.tags || []).map((t: any) => String(t.name || t).toLowerCase().trim()));
        return selected.every(t => tagSet.has(t));
      });
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter((m: any) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.location || '').toLowerCase().includes(q) ||
        (m.model_type || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [models, tagFilter, searchText]);

  const openAddTag = (modelId: string) => {
    setTagTargetModelId(modelId);
    setTagInput('');
    setTagColor(COLOR_PALETTE[0]);
    setTagDialogOpen(true);
  };
  const closeAddTag = () => {
    setTagDialogOpen(false);
    setTagTargetModelId(null);
    setTagInput('');
    setTagColor(COLOR_PALETTE[0]);
  };
  const submitAddTag = async () => {
    if (!tagTargetModelId || !tagInput.trim()) return;
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${BACKEND_URL}/api/user_models/${tagTargetModelId}/tags`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tag_name: tagInput.trim(), tag_color: tagColor })
      });
      if (!r.ok) {
        return;
      }
      const created = await r.json();
      const appliedColor = created.tag_color;
      const appliedName = created.tag_name;
      setUserModels((prev: any[]) =>
        prev.map((m: any) => {
          let tags = m.model_tags || [];
          // Add the new tag to the target model
          if (m.id === tagTargetModelId) {
            tags = [...tags, created];
          }
          // Propagate color to all tags with the same name across all models
          if (appliedColor) {
            tags = tags.map((t: any) =>
              t.tag_name === appliedName ? { ...t, tag_color: appliedColor } : t
            );
          }
          return { ...m, model_tags: tags };
        })
      );
      setTagInput('');
      setTagColor(COLOR_PALETTE[0]);
      // The backend may have auto-created a definition for a new tag name —
      // refresh so it appears (numbered) in the options/order immediately.
      fetchTagDefinitions();
    } catch (e) {
      // keep dialog open on error
    }
  };
  const selectedModelActiveTags = React.useMemo(() => {
    if (!tagTargetModelId) return [];
    const m = (userModels || []).find((x: any) => x.id === tagTargetModelId);
    return (m?.model_tags || []).filter((t: any) => t && t.status !== 'removed');
  }, [userModels, tagTargetModelId]);
  // Tag dialog options: active definitions in pipeline order (stages first,
  // numbered; labels after), excluding tags already on this model.
  const filteredTagOptions = React.useMemo<TagOption[]>(() => {
    const selected = new Set<string>(
      selectedModelActiveTags.map((t: any) => String(t.tag_name || '').toLowerCase().trim())
    );
    const opts: TagOption[] = [];
    orderedDefinitions.stages.forEach((d, i) => {
      if (!selected.has(d.name.toLowerCase().trim())) {
        opts.push({ name: d.name, color: d.color, isStage: true, stageNumber: i + 1 });
      }
    });
    orderedDefinitions.labels.forEach((d) => {
      if (!selected.has(d.name.toLowerCase().trim())) {
        opts.push({ name: d.name, color: d.color, isStage: false, stageNumber: null });
      }
    });
    return opts;
  }, [orderedDefinitions, selectedModelActiveTags]);
  const isDuplicateTag = React.useMemo(() => {
    const ti = tagInput.trim().toLowerCase();
    if (!ti) return false;
    return selectedModelActiveTags.some((t: any) => String(t.tag_name || '').toLowerCase().trim() === ti);
  }, [tagInput, selectedModelActiveTags]);
  const removeTag = async (tagId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${BACKEND_URL}/api/tags/${tagId}/remove`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!r.ok) return;
      setUserModels((prev: any[]) =>
        prev.map((m: any) =>
          m.id === tagTargetModelId
            ? {
                ...m,
                model_tags: (m.model_tags || []).map((t: any) => (t.id === tagId ? { ...t, status: 'removed' } : t))
              }
            : m
        )
      );
    } catch {}
  };
  const updateTagColor = async (tagId: string, newColor: string, tagName: string) => {
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${BACKEND_URL}/api/tags/${tagId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tag_color: newColor })
      });
      if (!r.ok) return;
      // Propagate color to ALL tags with the same name across all models
      setUserModels((prev: any[]) =>
        prev.map((m: any) => ({
          ...m,
          model_tags: (m.model_tags || []).map((t: any) =>
            t.tag_name === tagName ? { ...t, tag_color: newColor } : t
          )
        }))
      );
    } catch {}
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const parseValue = (value: any, property: string) => {
    if (value === 'N/A') return null;

    switch (property) {
      case 'irr':
        return value ? parseFloat(value.replace('%', '')) : null;
      case 'moic':
        return value ? parseFloat(value.replace('x', '')) : null;
      case 'created_at':
      case 'updated_at': {
        const n = typeof value === 'number' ? value : (value ? new Date(value).getTime() : null);
        return n != null && Number.isFinite(n) ? n : null;
      }
      case 'version_count':
        return typeof value === 'number' ? value : null;
      default:
        return value;
    }
  };

  const sortedModels = React.useMemo(() => {
    return [...filteredModels].sort((a, b) => {
      // Pipeline stage order: by the deal's current-stage sort_order. Deals
      // with no current stage sort last regardless of direction.
      if (orderBy === 'stage') {
        const ao = a.current_stage_order;
        const bo = b.current_stage_order;
        if (ao == null && bo == null) return 0;
        if (ao == null) return 1;
        if (bo == null) return -1;
        return order === 'asc' ? ao - bo : bo - ao;
      }

      // "Most recently updated": primary = updated_at, secondary = created_at (when null or equal)
      if (orderBy === 'updated_at') {
        const aPrimary = parseValue(a.updated_at_raw, 'updated_at');
        const bPrimary = parseValue(b.updated_at_raw, 'updated_at');
        const aSecondary = a.created_at_raw ?? 0;
        const bSecondary = b.created_at_raw ?? 0;
        if (aPrimary === null && bPrimary === null) {
          return order === 'desc' ? bSecondary - aSecondary : aSecondary - bSecondary;
        }
        if (aPrimary === null) return 1;
        if (bPrimary === null) return -1;
        const primaryDiff = order === 'desc' ? (bPrimary as number) - (aPrimary as number) : (aPrimary as number) - (bPrimary as number);
        if (primaryDiff !== 0) return primaryDiff;
        return order === 'desc' ? bSecondary - aSecondary : aSecondary - bSecondary;
      }

      const sortKey = orderBy === 'created_at' ? 'created_at_raw' : orderBy;
      const aValue = parseValue(a[sortKey], orderBy);
      const bValue = parseValue(b[sortKey], orderBy);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredModels, order, orderBy]);

  const handleEdit = async (modelId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${BACKEND_URL}/api/user_models/${modelId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await res.json();
      const versionId = data?.version_id;
      if (versionId) {
        navigate(`/edit-model/${versionId}`);
      } else {
        navigate(`/models/${modelId}`);
      }
    } catch {
      navigate(`/models/${modelId}`);
    }
  };

  const requestDelete = (modelId: string, modelName?: string) => {
    setPendingDeleteId(modelId);
    setPendingDeleteName(modelName || null);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BACKEND_URL}/api/user_models/${pendingDeleteId}/active`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: false })
      });
      setUserModels((prev: any[]) => prev.map((m: any) => m.id === pendingDeleteId ? { ...m, active: false } : m));
    } catch (e) {
      console.error('Failed to deactivate model', e);
    } finally {
      setConfirmOpen(false);
      setPendingDeleteId(null);
      setPendingDeleteName(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
    setPendingDeleteName(null);
  };


  const showModelsUi = !needsOnboarding;
  const isFiltering = (tagFilter.length > 0 || searchText.trim().length > 0) && filteredModels.length !== models.length;

  return (
    <>
      {/* Command bar — full-width, flush with sidebar and top */}
      {showModelsUi && models.length > 0 && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          rowGap: 1,
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, md: 0 },
          height: { xs: 'auto', md: `${APP_TOP_BAR_HEIGHT * 8}px` },
          ...topBarSx,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', mr: 0.5 }}>
            Your Models
          </Typography>
          <Box sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 12,
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'inline-flex',
            alignItems: 'center',
          }}>
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>
              {models.length} deal{models.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          {isFiltering && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
              Showing {filteredModels.length}
            </Typography>
          )}

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />

          {/* Search input */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.18)',
            borderRadius: `${theme.shape.borderRadius}px`,
            px: 1.5,
            height: 36,
            flex: { xs: '1 1 100%', md: '0 0 auto' },
            minWidth: { xs: 0, md: 160 },
            maxWidth: { xs: '100%', md: 220 },
            '&:focus-within': { backgroundColor: 'rgba(255,255,255,0.28)' },
          }}>
            <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, mr: 0.75 }} />
            <InputBase
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                color: '#fff',
                fontSize: '0.8125rem',
                flex: 1,
                height: 36,
                '& ::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 },
              }}
            />
          </Box>

          {/* Tag filter — desktop only */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 180, maxWidth: 240 }}>
            <Autocomplete
              multiple
              size="small"
              options={orderedFilterOptions}
              value={tagFilter}
              onChange={(_e, val) => setTagFilter(val as string[])}
              renderOption={(props, option) => {
                const { key, ...liProps } = props as any;
                const tc = colorForTag(option);
                const num = stageNumberByName.get(String(option).toLowerCase().trim());
                return (
                  <li key={option} {...liProps}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: tc, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{option}</Typography>
                      {num != null && (
                        <Typography variant="caption" color="text.secondary">Stage {num}</Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tc = colorForTag(option);
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: tc, ml: 0.75, flexShrink: 0 }} />}
                      sx={{
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        color: '#fff',
                        height: 22,
                        fontSize: '0.7rem',
                        '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', fontSize: 14 },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={tagFilter.length === 0 ? "Filter tags" : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontSize: '0.8125rem',
                      height: 36,
                      py: '0px !important',
                      '& fieldset': { border: 'none' },
                      '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 },
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Sort dropdown */}
          <Select
            value={`${orderBy}_${order}`}
            onChange={(e) => {
              const raw = e.target.value as string;
              const parts = raw.split('_');
              const dir = parts.pop() ?? 'desc';
              const field = parts.join('_');
              setOrderBy(field);
              setOrder(dir as 'asc' | 'desc');
            }}
            renderValue={(v) => {
              const labels: Record<string, string> = {
                created_at_desc: 'Newest first',
                created_at_asc: 'Oldest first',
                updated_at_desc: 'Most recently updated',
                stage_asc: 'Stage (pipeline order)',
                stage_desc: 'Stage (reverse order)',
                name_asc: 'Name A–Z',
                name_desc: 'Name Z–A',
                irr_desc: 'IRR (high–low)',
                irr_asc: 'IRR (low–high)',
                moic_desc: 'MOIC (high–low)',
                moic_asc: 'MOIC (low–high)',
              };
              return labels[String(v)] ?? String(v);
            }}
            size="small"
            startAdornment={<SortIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, mr: 0.5 }} />}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.8125rem',
              height: 36,
              minWidth: 140,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.7)' },
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
            }}
            MenuProps={{
              PaperProps: {
                sx: { mt: 0.5, '& .MuiMenuItem-root': { fontSize: '0.8125rem' } }
              }
            }}
          >
            <MenuItem value="created_at_desc">Newest first</MenuItem>
            <MenuItem value="created_at_asc">Oldest first</MenuItem>
            <MenuItem value="updated_at_desc">Most recently updated</MenuItem>
            <MenuItem value="stage_asc">Stage (pipeline order)</MenuItem>
            <MenuItem value="stage_desc">Stage (reverse order)</MenuItem>
            <MenuItem value="name_asc">Name A–Z</MenuItem>
            <MenuItem value="name_desc">Name Z–A</MenuItem>
            <MenuItem value="irr_desc">IRR (high–low)</MenuItem>
            <MenuItem value="irr_asc">IRR (low–high)</MenuItem>
            <MenuItem value="moic_desc">MOIC (high–low)</MenuItem>
            <MenuItem value="moic_asc">MOIC (low–high)</MenuItem>
          </Select>

          {/* View toggles — desktop only (mobile defaults to cards) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setView('cards')}
              sx={{
                color: '#fff',
                backgroundColor: view === 'cards' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                borderRadius: 1,
                width: 36,
                height: 36,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
              }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setView('table')}
              sx={{
                color: '#fff',
                backgroundColor: view === 'table' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                borderRadius: 1,
                width: 36,
                height: 36,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
              }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Create Model button */}
          {canEdit && (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/create-model')}
            sx={{
              backgroundColor: colors.blue,
              color: '#fff',
              fontWeight: 600,
              px: 2,
              height: 36,
              whiteSpace: 'nowrap',
              width: { xs: '100%', md: 'auto' },
              '&:hover': { backgroundColor: colors.blueDark },
            }}
          >
            + Create Model
          </Button>
          )}
        </Box>
      )}

      <Container maxWidth={false} sx={{ mt: showModelsUi && models.length > 0 ? 3 : { xs: 2, md: 6 }, mx: 'auto', px: { xs: 1.5, sm: 3 } }}>
        {/* Onboarding gate */}
        {needsOnboarding && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            {onboardingStep === 1 ? (
              <OnboardingStepOne
                initialData={onboardingData || {}}
                onSkip={() => setNeedsOnboarding(false)}
                onNext={(d) => { setOnboardingData({ ...(onboardingData || {}), ...d }); setOnboardingStep(2); }}
              />
            ) : onboardingStep === 2 ? (
              <OnboardingStepTwo
                initialData={onboardingData || { assetTypeFocus: [], typicalDealSize: '', geographicFocus: '', referralSource: '', emailUpdates: false }}
                onSkip={() => setNeedsOnboarding(false)}
                onComplete={(d) => handleOnboardingComplete({ ...(onboardingData || {}), ...d })}
              />
            ) : (
              <OnboardingStepThree
                onBack={() => setOnboardingStep(2)}
                onSkip={() => setNeedsOnboarding(false)}
                // Fires for Freemium immediately, and for paid plans only after
                // the subscription actually starts.
                onChoosePlan={() => setNeedsOnboarding(false)}
              />
            )}
          </Box>
        )}

        {subLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <ConstructionLoader />
          </Box>
        )}


        {showModelsUi && models.length == 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
            <Paper sx={{ p: 4, maxWidth: 640, textAlign: 'center', mx: 'auto' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                No models found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {canEdit
                  ? "You haven't created any models yet. Click below to get started!"
                  : 'Your firm has no deals yet. They will appear here as your colleagues create them.'}
              </Typography>
              {canEdit && (
                <Button variant="contained" onClick={() => navigate('/create-model')}>
                  Create Model
                </Button>
              )}
            </Paper>
          </Box>
        )}

        {showModelsUi && models.length > 0 && (view === 'cards' ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'center' } }}>
          {sortedModels.map((model: any, index: number) => {
            // Accent the card with the current pipeline stage when present,
            // otherwise the first tag (legacy behavior).
            const firstTag = model.tags && model.tags.length > 0 ? model.tags[0] : null;
            const accentColor = model.current_stage
              ? colorForTag(model.current_stage.name, model.current_stage.color)
              : (firstTag ? colorForTag(firstTag.name, firstTag.color) : colors.grey[300]);
            return (
            <Paper
              key={index}
              onClick={() => navigate(`/models/${model.id}`)}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: '320px' },
                minWidth: { xs: 0, md: '250px' },
                mb: 2,
                borderRadius: `${theme.shape.borderRadius}px`,
                border: `1px solid ${colors.grey[300]}`,
                boxShadow: theme.shadows[1],
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[2],
                },
                '& .model-actions': {
                  opacity: 0,
                  visibility: 'hidden',
                  transition: 'opacity 200ms ease, visibility 200ms ease',
                },
                '&:hover .model-actions': {
                  opacity: 1,
                  visibility: 'visible',
                }
              }}
            >
              {/* Top accent bar */}
              <Box sx={{ height: 4, backgroundColor: accentColor }} />

              {/* Card body */}
              <Box sx={{ p: 2.5, pb: { xs: 2.5, md: 0 }, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                {/* Left column: name, location, model type, tags */}
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Name */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '1rem',
                    lineHeight: 1.3,
                    mb: 0.5,
                  }}
                >
                  {model.name}
                </Typography>

                {/* Current pipeline stage — shown prominently */}
                {model.current_stage && (() => {
                  const sc = colorForTag(model.current_stage.name, model.current_stage.color);
                  return (
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      alignSelf: 'flex-start',
                      px: 1,
                      py: 0.4,
                      mb: 0.5,
                      borderRadius: 10,
                      backgroundColor: sc,
                      maxWidth: '100%',
                    }}>
                      {model.current_stage.stageNumber != null && (
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff',
                          fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {model.current_stage.stageNumber}
                        </Box>
                      )}
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {model.current_stage.name}
                      </Typography>
                    </Box>
                  );
                })()}

                {/* Location + Model type on same row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1,mb: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: colors.grey[400] }} />
                  <Typography variant="body2" sx={{ color: colors.grey[600], fontSize: '0.8rem', flex: 1 }}>
                    {model.location}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  {model.model_type && (
                    <Box sx={{
                      px: 1.25,
                      py: 0.25,
                      borderRadius: 1,
                      border: `1px solid ${colors.grey[300]}`,
                      backgroundColor: colors.white,
                      flexShrink: 0,
                    }}>
                      <Typography variant="caption" sx={{ fontSize: '0.72rem', color: colors.grey[700], whiteSpace: 'nowrap', fontWeight: 500 }}>
                        {model.model_type}
                      </Typography>
                    </Box>
                  )}
                  {user?.plan_tier === 'freemium' && 
                   ['past_due', 'canceled', 'unpaid'].includes(subscriptionStatus || '') && 
                   ['Mixed-Use', 'Industrial / Retail', 'Multifamily and Mixed-Use Development'].includes(model.model_type) && (
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      backgroundColor: '#FEF3C7', // amber-100
                      border: '1px solid #F59E0B', // amber-500
                      flexShrink: 0,
                    }}>
                      <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#B45309', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        Payment issue
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Secondary tag chips (everything except the current stage) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', flex: { xs: '0 0 auto', md: 1 } }}>
                  {model.other_tags && model.other_tags.length > 0 ? (
                    model.other_tags.map((tag: any, i: number) => {
                      const tc = colorForTag(tag.name, tag.color);
                      return (
                        <Box
                          key={tag.id || i}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 0.75,
                            py: 0.2,
                            borderRadius: 10,
                            backgroundColor: `${tc}14`,
                            border: `1px solid ${tc}30`,
                          }}
                        >
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: tc,
                            flexShrink: 0,
                          }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: colors.grey[700], lineHeight: 1 }}>
                            {tag.name}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : null}
                  {canEdit && (
                  <Tooltip title="Edit Tags">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); openAddTag(model.id); }}
                      sx={{
                        width: 24,
                        height: 24,
                        border: `1.5px dashed ${colors.grey[300]}`,
                        borderRadius: '50%',
                        p: 0,
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: colors.blueTint,
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14, color: colors.grey[400] }} />
                    </IconButton>
                  </Tooltip>
                  )}
                </Box>

                {/* Created at / Last updated — hidden on mobile to keep cards compact */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.75, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem' }}>Created at</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: colors.grey[700] }}>{model.created_at}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem' }}>Last updated</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: colors.grey[700] }}>{model.updated_at}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem' }}>Versions:</Typography>
                    <Typography className="num" variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: colors.grey[700] }}>{model.version_count}</Typography>
                  </Box>
                  {inFirm && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem', flexShrink: 0 }}>Created by</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', color: colors.grey[700], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.created_by}</Typography>
                    </Box>
                  )}
                </Box>
                </Box>{/* end left column */}

                {/* Mobile metrics — IRR / MOIC to the right of the content */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', justifyContent: 'center', gap: 1, flexShrink: 0, textAlign: 'right' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem', display: 'block' }}>IRR</Typography>
                    <Typography className="num" variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.blue, lineHeight: 1.2 }}>{model.irr}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem', display: 'block' }}>MOIC</Typography>
                    <Typography className="num" variant="body1" sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.blue, lineHeight: 1.2 }}>{model.moic}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Bottom metrics row — desktop only */}
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                borderTop: `1px solid ${colors.grey[300]}`,
                mt: 2,
              }}>
                <Box sx={{ flex: 1, py: 1.5, textAlign: 'center', borderRight: `1px solid ${colors.grey[300]}` }}>
                  <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem', display: 'block' }}>
                    IRR
                  </Typography>
                  <Typography className="num" variant="body1" sx={{ fontWeight: 600, fontSize: '1rem', color: colors.blue, lineHeight: 1.3 }}>
                    {model.irr}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, py: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: '0.68rem', display: 'block' }}>
                    MOIC
                  </Typography>
                  <Typography className="num" variant="body1" sx={{ fontWeight: 600, fontSize: '1rem', color: colors.blue, lineHeight: 1.3 }}>
                    {model.moic}
                  </Typography>
                </Box>
              </Box>

              {/* Hover overlay with View / Edit / Delete */}
              <Box
                className="model-actions"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  backgroundColor: colors.navy,
                  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
                }}
              >
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); navigate(`/models/${model.id}`); }}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    px: 0,
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  View Model
                </Button>
                <Box sx={{ flex: 1 }} />
                {canEdit && (
                  <>
                    <IconButton aria-label="edit" size="small" onClick={(e) => { e.stopPropagation(); handleEdit(model.id); }} sx={{ color: '#fff', p: 0.5 }}>
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton aria-label="delete" size="small" onClick={(e) => { e.stopPropagation(); requestDelete(model.id, model.name); }} sx={{ color: '#fff', p: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: `${theme.shape.borderRadius}px`,
            border: `1px solid ${colors.grey[300]}`,
            boxShadow: theme.shadows[1],
            overflowX: 'auto',
            width: '100%'
          }}
        >
          <Table
            sx={{
              minWidth: { xs: 900, md: 0 },
              '& thead th': {
                fontWeight: 600,
                fontSize: '0.7rem',
                color: colors.grey[600],
                backgroundColor: colors.grey[100],
                borderBottom: `1px solid ${colors.grey[300]}`,
                py: 1.5,
                whiteSpace: 'nowrap',
              },
              '& tbody td': {
                borderBottom: `1px solid ${colors.grey[300]}`,
                py: 1.5,
                whiteSpace: 'nowrap',
              },
              '& tbody tr:hover': {
                backgroundColor: colors.blueTint,
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160 }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'location'}
                    direction={orderBy === 'location' ? order : 'asc'}
                    onClick={() => handleRequestSort('location')}
                  >
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'model_type'}
                    direction={orderBy === 'model_type' ? order : 'asc'}
                    onClick={() => handleRequestSort('model_type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'irr'}
                    direction={orderBy === 'irr' ? order : 'asc'}
                    onClick={() => handleRequestSort('irr')}
                  >
                    IRR
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'moic'}
                    direction={orderBy === 'moic' ? order : 'asc'}
                    onClick={() => handleRequestSort('moic')}
                  >
                    MOIC
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                {inFirm && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'created_by'}
                      direction={orderBy === 'created_by' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_by')}
                    >
                      Created by
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  Tags
                </TableCell>
                <TableCell sx={{ width: 100 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedModels.map((model: any, index: number) => (
                <TableRow
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    '& .row-actions': {
                      opacity: 0,
                      visibility: 'hidden',
                      transition: 'opacity 120ms ease, visibility 120ms ease'
                    },
                    '&:hover .row-actions': {
                      opacity: 1,
                      visibility: 'visible'
                    }
                  }}
                  onClick={() => navigate(`/models/${model.id}`)}
                >
                  <TableCell sx={{ fontWeight: 600, color: colors.grey[900] }}>{model.name}</TableCell>
                  <TableCell sx={{ color: colors.grey[700] }}>{model.location}</TableCell>
                  <TableCell>
                    {model.model_type ? (
                      <Box sx={{
                        display: 'inline-block',
                        px: 1.25,
                        py: 0.25,
                        borderRadius: 1,
                        border: `1px solid ${colors.grey[300]}`,
                        backgroundColor: colors.grey[100],
                      }}>
                        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: colors.grey[700], fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {model.model_type}
                        </Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.blue }}>{model.irr}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.blue }}>{model.moic}</TableCell>
                  <TableCell sx={{ color: colors.grey[700] }}>{model.created_date}</TableCell>
                  {inFirm && (
                    <TableCell sx={{ color: colors.grey[700], maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {model.created_by}
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'nowrap', alignItems: 'center' }}>
                      {/* Current stage first, filled and (where applicable) numbered */}
                      {model.current_stage && (() => {
                        const sc = colorForTag(model.current_stage.name, model.current_stage.color);
                        return (
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1, py: 0.25, borderRadius: 10, backgroundColor: sc, flexShrink: 0,
                          }}>
                            {model.current_stage.stageNumber != null && (
                              <Box sx={{
                                width: 15, height: 15, borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff',
                                fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {model.current_stage.stageNumber}
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#fff', fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {model.current_stage.name}
                            </Typography>
                          </Box>
                        );
                      })()}
                      {model.other_tags && model.other_tags.length > 0 ? model.other_tags.map((tag: any, i: number) => {
                        const tc = colorForTag(tag.name, tag.color);
                        return (
                          <Box
                            key={tag.id || i}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 10,
                              backgroundColor: `${tc}14`,
                              border: `1px solid ${tc}30`,
                              flexShrink: 0,
                            }}
                          >
                            <Box sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              backgroundColor: tc,
                              flexShrink: 0,
                            }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: colors.grey[700], lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {tag.name}
                            </Typography>
                          </Box>
                        );
                      }) : null}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    <Box className="row-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                        <Tooltip title="View">
                          <IconButton aria-label="view" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/models/${model.id}`); }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton aria-label="edit" size="small" onClick={(e) => { e.stopPropagation(); handleEdit(model.id); }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton aria-label="delete" size="small" color="error" onClick={(e) => { e.stopPropagation(); requestDelete(model.id, model.name); }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      <Dialog open={confirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to remove model{pendingDeleteName ? ` "${pendingDeleteName}"` : ''}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>Remove</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={tagDialogOpen} onClose={closeAddTag} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Edit Tags</DialogTitle>
        <DialogContent>
          {/* Add new tag section */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Add a Tag
          </Typography>
          <Autocomplete<TagOption, false, false, true>
            freeSolo
            options={filteredTagOptions}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
            filterOptions={(opts, state) => {
              const q = state.inputValue.toLowerCase().trim();
              if (!q) return opts;
              return opts.filter((o) => o.name.toLowerCase().includes(q));
            }}
            inputValue={tagInput}
            onInputChange={(_e, v) => setTagInput(v || '')}
            onChange={(_e, val) => {
              if (val && typeof val !== 'string') {
                setTagInput(val.name);
                if (val.color) setTagColor(val.color);
              } else if (typeof val === 'string') {
                setTagInput(val);
              }
            }}
            renderOption={(props, option) => {
              const { key, ...liProps } = props as any;
              const c = option.color || getTagColor(option.name);
              return (
                <li key={option.name} {...liProps}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {option.isStage ? (
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', backgroundColor: c, color: '#fff',
                        fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {option.stageNumber}
                      </Box>
                    ) : (
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: c, flexShrink: 0 }} />
                    )}
                    <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.isStage ? `Stage ${option.stageNumber}` : 'Label'}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} autoFocus margin="dense" label="Tag name" type="text" fullWidth size="small" />
            )}
          />
          {isDuplicateTag && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              This tag is already added to this model.
            </Typography>
          )}

          {/* Color palette */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, mb: 0.75, display: 'block' }}>
            Choose a color
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            {COLOR_PALETTE.map((c) => (
              <Box
                key={c}
                onClick={() => setTagColor(c)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: c,
                  cursor: 'pointer',
                  border: tagColor === c ? '3px solid' : '2px solid transparent',
                  borderColor: tagColor === c ? colors.navy : 'transparent',
                  outline: tagColor === c ? `2px solid ${c}` : 'none',
                  outlineOffset: 1,
                  transition: 'transform 100ms ease',
                  '&:hover': { transform: 'scale(1.15)' },
                }}
              />
            ))}
          </Box>

          {/* Preview + Add button */}
          {tagInput.trim() && !isDuplicateTag && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 10,
                backgroundColor: `${tagColor}14`,
                border: `1px solid ${tagColor}30`,
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: tagColor, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: colors.grey[700], lineHeight: 1 }}>
                  {tagInput.trim()}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={submitAddTag}
                disabled={!tagInput.trim() || !tagTargetModelId || isDuplicateTag}
                sx={{ ml: 'auto', textTransform: 'none', fontWeight: 600 }}
              >
                Add Tag
              </Button>
            </Box>
          )}

          {/* Divider */}
          <Box sx={{ borderTop: `1px solid ${colors.grey[300]}`, mt: 1, pt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Current Tags
            </Typography>
            {selectedModelActiveTags.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedModelActiveTags.map((t: any) => {
                  const currentColor = getTagColor(t.tag_name, t.tag_color);
                  return (
                    <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Color dot — clickable to cycle through palette */}
                      <Tooltip title="Change color">
                        <Box
                          onClick={() => {
                            const idx = COLOR_PALETTE.indexOf(currentColor);
                            const nextColor = COLOR_PALETTE[(idx + 1) % COLOR_PALETTE.length];
                            updateTagColor(t.id, nextColor, t.tag_name);
                          }}
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: currentColor,
                            cursor: 'pointer',
                            flexShrink: 0,
                            border: `2px solid ${currentColor}50`,
                            '&:hover': { transform: 'scale(1.2)', transition: 'transform 100ms ease' },
                          }}
                        />
                      </Tooltip>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {t.tag_name}
                      </Typography>
                      <IconButton size="small" onClick={() => removeTag(t.id)} sx={{ p: 0.25 }}>
                        <DeleteIcon sx={{ fontSize: 16, color: colors.grey[400] }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No tags yet</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddTag}>Done</Button>
        </DialogActions>
      </Dialog>
      </Container>
    </>
  );
};

export default Home;
